import { useState, type ReactNode } from 'react';
import { Input } from '../ui';
import { screenLines, impliedPriceAtCap, priceGapAtCap } from '../../utils/quickScreen';

/**
 * The first question: is this deal worth a second hour?
 *
 * Joseph, 2026-09-08: *"a way to easily put in key information that can basically tell us if deal
 * is good or not and we can always adjust after that."*
 *
 * ⭐⭐⭐ IT OWNS NOTHING. Every input edits the analyzer's own field and every figure below is the
 * analyzer's own computed value, passed in. There is no second formula for NOI here, because two
 * models of one deal is precisely the failure this screen was born out of - 13368 Main Street
 * printed a 7.46% cap against an OM stating 5.75%.
 *
 * ⛔⛔ AND THE ROWS FOOT. The delivered PDF for that deal showed $383,024 less $14,555 and called
 * the answer $349,318 - a 5% vacancy applied and never printed. Every subtraction between the top
 * line and NOI is a row here, so the one table a buyer checks by hand can be checked by hand.
 */

export interface QuickScreenProps {
  /** Editable analyzer fields, exactly as the analyzer holds them. */
  values: {
    purchase_price?: number | string | null;
    potential_gross_income?: number | string | null;
    vacancy_rate?: number | string | null;
    property_taxes?: number | string | null;
    insurance?: number | string | null;
    repairs_maintenance?: number | string | null;
    management_fee_percent?: number | string | null;
  };
  /** Computed by the analyzer and passed in - never recomputed here. */
  computed: {
    pgi: number;
    vacancyAmount: number;
    otherIncome: number;
    reimbursements: number;
    totalExpenses: number;
    noi: number;
    capRate: number;
    purchasePrice: number;
  };
  onChange: (field: string, value: string) => void;
  /** The analyzer's provenance dot, so a figure read from the OM is visible as one. */
  renderSourceDot?: (field: string) => ReactNode;
}

const money = (n: number | null): string =>
  n === null || !Number.isFinite(n) ? '—' : `$${Math.round(n).toLocaleString('en-US')}`;

const signedMoney = (n: number): string =>
  n < 0 ? `(${money(Math.abs(n))})` : money(n);

export default function QuickScreen({ values, computed, onChange, renderSourceDot }: QuickScreenProps) {
  const [targetCap, setTargetCap] = useState('6.50');

  const lines = screenLines({
    pgi: computed.pgi,
    vacancyAmount: computed.vacancyAmount,
    otherIncome: computed.otherIncome,
    reimbursements: computed.reimbursements,
    totalExpenses: computed.totalExpenses,
    noi: computed.noi,
  });

  const target = parseFloat(targetCap);
  const implied = impliedPriceAtCap(computed.noi, target);
  const gap = priceGapAtCap(computed.purchasePrice, computed.noi, target);

  const field = (name: keyof QuickScreenProps['values'], label: string, addon: string) => (
    <div>
      <label className="block text-[11px] font-medium text-gray-600 mb-1">
        {label}{renderSourceDot?.(name as string)}
      </label>
      <Input
        type="number"
        leftAddon={addon}
        value={(values[name] as string | number | null | undefined) ?? ''}
        onChange={(e) => onChange(name as string, e.target.value)}
      />
    </div>
  );

  return (
    <div className="mb-6 rounded-xl border border-gray-300 bg-white p-4" data-quick-screen>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[.14em] text-primary-500">
          Quick screen
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] text-gray-500">Going-in cap at asking</span>
          <span
            className="text-2xl font-extrabold text-gray-900"
            data-quick-screen-cap
          >
            {Number.isFinite(computed.capRate) && computed.capRate > 0
              ? `${computed.capRate.toFixed(2)}%`
              : '—'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {field('purchase_price', 'Asking price', '$')}
        {field('potential_gross_income', 'Gross income', '$')}
        {field('property_taxes', 'Property taxes', '$')}
        {field('insurance', 'Insurance', '$')}
        {field('repairs_maintenance', 'CAM / repairs', '$')}
        {field('vacancy_rate', 'Vacancy', '%')}
        {field('management_fee_percent', 'Management', '%')}
      </div>

      {/* ⛔ Every subtraction between the top line and NOI is a row. */}
      <div className="mt-4 border-t border-gray-200 pt-3">
        {lines.map((l) => (
          <div
            key={l.label}
            className={`flex items-baseline justify-between py-1 text-[12.5px] ${
              l.kind === 'total'
                ? 'mt-1 border-t border-gray-300 pt-2 font-bold text-gray-900'
                : 'text-gray-700'
            }`}
          >
            <span>{l.label}</span>
            <span className={l.kind === 'deduction' ? 'text-gray-600' : ''}>
              {signedMoney(l.amount)}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-gray-200 pt-3">
        <div className="w-28">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Your target cap</label>
          <Input
            type="number"
            leftAddon="%"
            value={targetCap}
            onChange={(e) => setTargetCap(e.target.value)}
          />
        </div>
        <div className="text-[12.5px]">
          <div className="text-gray-600">Pays up to</div>
          <div className="text-lg font-bold text-gray-900" data-quick-screen-implied>
            {money(implied)}
          </div>
        </div>
        {gap !== null && (
          <div className="text-[12.5px]">
            <div className="text-gray-600">{gap >= 0 ? 'Under asking by' : 'Over asking by'}</div>
            <div className={`text-lg font-bold ${gap >= 0 ? 'text-success' : 'text-warning'}`}>
              {money(Math.abs(gap))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
